<template>
  <ui-popover :open="isOpen">
    <ui-popover-anchor as-child>
      <div :id="inputProps.id" class="relative w-full">
        <ui-input
          v-bind="inputAttrs"
          :model-value="modelText"
          :class="loading ? 'pr-9' : ''"
          autocomplete="off"
          role="combobox"
          :aria-expanded="isOpen"
          :aria-controls="listboxId"
          @input="onInput"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="close"
          @focus="onFocus"
          @blur="onBlur"
        />
        <loader-circle-icon v-if="loading" class="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-primary" />
      </div>
    </ui-popover-anchor>

    <!-- Portaled, so the list is not hidden under the next glass block (own stacking context). Focus stays in the input. -->
    <ui-popover-content
      align="start"
      class="w-(--reka-popover-trigger-width) p-1"
      @open-auto-focus.prevent
      @close-auto-focus.prevent
    >
      <ul
        :id="listboxId"
        role="listbox"
        class="m-0 max-h-56 list-none overflow-y-auto p-0"
      >
        <li
          v-for="(suggestion, index) in flatSuggestions"
          :key="'autosuggest-item-' + index"
          role="option"
          :aria-selected="index === highlightedIndex"
          :class="[ 'cursor-pointer truncate rounded-sm px-2 py-1.5 text-sm', index === highlightedIndex ? 'bg-accent text-accent-foreground' : '' ]"
          @mouseenter="highlightedIndex = index"
          @mousedown.prevent="selectSuggestion(suggestion)"
        >
          <slot :suggestion="suggestion">
            {{ suggestion.item?.name ?? suggestion.item }}
          </slot>
        </li>
      </ul>
    </ui-popover-content>
  </ui-popover>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

/** Text input with a suggestion list (arrow keys, enter, escape); slot renders an item. */
export default {
  name: 'AutosuggestInput',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    modelValue: {
      type: [ String, Number ],
      default: undefined,
    },
    inputProps: {
      type: Object,
      default: () => ({}),
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: [ 'update:modelValue', 'selected' ],
  data() {
    return {
      hasFocus: false,
      highlightedIndex: -1,
    };
  },
  computed: {
    // The "autosuggest" prefix also keeps main.js's blur auto-trim off this input.
    listboxId() {
      return 'autosuggest-' + this.$.uid;
    },
    modelText() {
      return this.modelValue ?? '';
    },
    inputAttrs() {
      const { id: _id, ...rest } = this.inputProps;

      return rest;
    },
    flatSuggestions() {
      const items = [];
      for (const section of this.suggestions) {
        if (section && Array.isArray(section.data)) {
          items.push(...section.data);
        }
      }

      return items.map(item => ({ item }));
    },
    isOpen() {
      return this.hasFocus && this.flatSuggestions.length > 0;
    },
  },
  watch: {
    flatSuggestions() {
      this.highlightedIndex = -1;
    },
  },
  methods: {
    onInput(event) {
      if (document.activeElement !== event.target) {
        return;
      }

      this.hasFocus = true;

      this.$emit('update:modelValue', event.target.value);
    },
    moveHighlight(delta) {
      if (!this.isOpen) {
        return;
      }

      const count = this.flatSuggestions.length;
      this.highlightedIndex = (this.highlightedIndex + delta + count) % count;
    },
    onEnter() {
      if (this.isOpen && this.highlightedIndex >= 0) {
        this.selectSuggestion(this.flatSuggestions[this.highlightedIndex]);
      }

      this.close();
    },
    selectSuggestion(suggestion) {
      this.$emit('selected', suggestion);
      this.close();
    },
    close() {
      this.hasFocus = false;
      this.highlightedIndex = -1;
    },
    onFocus() {
      this.hasFocus = true;
    },
    onBlur() {
      this.hasFocus = false;
      this.highlightedIndex = -1;
    },
  },
};
</script>
